import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { StoreProvider } from "@/context/StoreContext";
import AppLayout from "@/components/AppLayout";
import { ForcePasswordChange } from "@/components/ForcePasswordChange";
import { Loading } from "@/components/common";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Orders from "@/pages/Orders";
import Advertising from "@/pages/Advertising";
import Complaints from "@/pages/Complaints";
import SalesAnalytics from "@/pages/SalesAnalytics";
import ProductVariants from "@/pages/ProductVariants";
import MasterCrud from "@/pages/MasterCrud";
import InventoryPusat from "@/pages/InventoryPusat";
import InventoryMaterials from "@/pages/InventoryMaterials";
import InventoryDashboard from "@/pages/InventoryDashboard";
import SaldoAwalInventory from "@/pages/SaldoAwalInventory";
import InventoryUmum from "@/pages/InventoryUmum";
import PemakaianInternal from "@/pages/PemakaianInternal";
import TransferStok from "@/pages/TransferStok";
import Reservasi from "@/pages/Reservasi";
import StockOpname from "@/pages/StockOpname";
import StockAdjustment from "@/pages/StockAdjustment";
import Returns from "@/pages/Returns";
import Suppliers from "@/pages/Suppliers";
import PurchaseRequests from "@/pages/PurchaseRequests";
import PurchaseOrders from "@/pages/PurchaseOrders";
import GoodsReceiving from "@/pages/GoodsReceiving";
import ProductionOrders from "@/pages/ProductionOrders";
import MaterialRequests from "@/pages/MaterialRequests";
import ProductionDashboard from "@/pages/ProductionDashboard";
import PurchasingDashboard from "@/pages/PurchasingDashboard";
import Alerts from "@/pages/Alerts";
import Reports from "@/pages/Reports";
import ChartOfAccounts from "@/pages/ChartOfAccounts";
import CashBank from "@/pages/CashBank";
import FinanceReports from "@/pages/FinanceReports";
import FinanceDashboard from "@/pages/FinanceDashboard";
import OwnerFinanceSummary from "@/pages/OwnerFinanceSummary";
import AssetRegister from "@/pages/AssetRegister";
import Budget from "@/pages/Budget";
import BankReconciliation from "@/pages/BankReconciliation";
import FinanceControl from "@/pages/FinanceControl";
import HrEmployees from "@/pages/HrEmployees";
import HrMasters from "@/pages/HrMasters";
import HrAttendance from "@/pages/HrAttendance";
import HrLeave from "@/pages/HrLeave";
import HrPayroll from "@/pages/HrPayroll";
import HrPaidDaysPolicy from "@/pages/HrPaidDaysPolicy";
import OmsDashboard from "@/pages/oms/OmsDashboard";
import ChatMonitoring from "@/pages/oms/ChatMonitoring";
import MonitoringToko from "@/pages/oms/MonitoringToko";
import DailyAssessment from "@/pages/oms/DailyAssessment";
import ReportHistory from "@/pages/oms/ReportHistory";
import DpDashboard from "@/pages/dp/DpDashboard";
import DpJobs from "@/pages/dp/DpJobs";
import DpServices from "@/pages/dp/DpServices";
import DpProductionQueue from "@/pages/dp/DpProductionQueue";
import DpQc from "@/pages/dp/DpQc";
import DpMaterial from "@/pages/dp/DpMaterial";
import DpConversions from "@/pages/dp/DpConversions";
import HrDashboard from "@/pages/HrDashboard";
import SupplierBills from "@/pages/SupplierBills";
import AccountsPayable from "@/pages/AccountsPayable";
import SupplierPayments from "@/pages/SupplierPayments";
import SalesReceivables from "@/pages/SalesReceivables";
import MarketplaceSettlement from "@/pages/MarketplaceSettlement";
import CostingDashboard from "@/pages/CostingDashboard";
import HppCalculator from "@/pages/HppCalculator";
import FabricPrices from "@/pages/FabricPrices";
import FabricYields from "@/pages/FabricYields";
import SewingCost from "@/pages/SewingCost";
import OptionalCosts from "@/pages/OptionalCosts";
import HppVersions from "@/pages/HppVersions";
import WaitingHpp from "@/pages/WaitingHpp";
import ActualCosting from "@/pages/ActualCosting";
import CatchWeightMigration from "@/pages/CatchWeightMigration";
import Journals from "@/pages/Journals";
import GeneralLedger from "@/pages/GeneralLedger";
import TrialBalance from "@/pages/TrialBalance";
import AccountingPeriods from "@/pages/AccountingPeriods";
import Users from "@/pages/Users";
import Roles from "@/pages/Roles";
import AuditLog from "@/pages/AuditLog";
import Settings from "@/pages/Settings";

function Protected({ children, perm }) {
  const { user, ready, can } = useAuth();
  if (!ready) return <div className="flex h-screen items-center justify-center"><Loading /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <ForcePasswordChange />;
  if (perm && !can(perm)) {
    return (
      <StoreProvider>
        <AppLayout>
          <div className="p-10 text-center" data-testid="not-authorized">
            <h1 className="text-2xl font-semibold text-white mb-2">Tidak Berwenang</h1>
            <p className="text-slate-400 text-sm">Anda tidak memiliki akses ke halaman ini.</p>
          </div>
        </AppLayout>
      </StoreProvider>
    );
  }
  return (
    <StoreProvider>
      <AppLayout>{children}</AppLayout>
    </StoreProvider>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<Protected><Dashboard /></Protected>} />
      <Route path="/orders" element={<Protected><Orders /></Protected>} />
      <Route path="/advertising" element={<Protected><Advertising /></Protected>} />
      <Route path="/complaints" element={<Protected><Complaints /></Protected>} />
      <Route path="/sales-analytics" element={<Protected><SalesAnalytics /></Protected>} />
      <Route path="/products" element={<Protected><ProductVariants /></Protected>} />
      <Route path="/master/:collection" element={<Protected><MasterCrud /></Protected>} />
      <Route path="/inventory/dashboard" element={<Protected perm="inventory.dashboard.view"><InventoryDashboard /></Protected>} />
      <Route path="/inventory/opening" element={<Protected perm="inventory.opening.view"><SaldoAwalInventory /></Protected>} />
      <Route path="/inventory/pusat" element={<Protected><InventoryPusat /></Protected>} />
      <Route path="/inventory/konveksi" element={<Protected><InventoryMaterials type="KONVEKSI" /></Protected>} />
      <Route path="/inventory/printing" element={<Protected><InventoryMaterials type="PRINTING" /></Protected>} />
      <Route path="/inventory/umum" element={<Protected perm="inventory.general.view"><InventoryUmum /></Protected>} />
      <Route path="/inventory/pemakaian-internal" element={<Protected perm="inventory.general.view"><PemakaianInternal /></Protected>} />
      <Route path="/inventory/transfer" element={<Protected><TransferStok /></Protected>} />
      <Route path="/inventory/reservasi" element={<Protected><Reservasi /></Protected>} />
      <Route path="/inventory/opname" element={<Protected><StockOpname /></Protected>} />
      <Route path="/inventory/adjustment" element={<Protected><StockAdjustment /></Protected>} />
      <Route path="/inventory/retur" element={<Protected><Returns /></Protected>} />
      <Route path="/inventory/catch-weight-migration" element={<Protected perm="inventory.catch_weight.migrate.view"><CatchWeightMigration /></Protected>} />
      <Route path="/purchasing/suppliers" element={<Protected><Suppliers /></Protected>} />
      <Route path="/purchasing/requests" element={<Protected><PurchaseRequests /></Protected>} />
      <Route path="/purchasing/orders" element={<Protected><PurchaseOrders /></Protected>} />
      <Route path="/purchasing/receiving" element={<Protected><GoodsReceiving /></Protected>} />
      <Route path="/production/orders" element={<Protected><ProductionOrders /></Protected>} />
      <Route path="/production/material-requests" element={<Protected><MaterialRequests /></Protected>} />
      <Route path="/production/dashboard" element={<Protected><ProductionDashboard /></Protected>} />
      <Route path="/purchasing/dashboard" element={<Protected><PurchasingDashboard /></Protected>} />
      <Route path="/digital-printing/dashboard" element={<Protected perm="digital_printing.dashboard.view"><DpDashboard /></Protected>} />
      <Route path="/digital-printing/jobs" element={<Protected perm="digital_printing.job.view"><DpJobs /></Protected>} />
      <Route path="/digital-printing/services" element={<Protected perm="digital_printing.service.view"><DpServices /></Protected>} />
      <Route path="/digital-printing/queue" element={<Protected perm="digital_printing.production.view"><DpProductionQueue /></Protected>} />
      <Route path="/digital-printing/qc" element={<Protected perm="digital_printing.qc.view"><DpQc /></Protected>} />
      <Route path="/digital-printing/material" element={<Protected perm="digital_printing.material.view"><DpMaterial /></Protected>} />
      <Route path="/digital-printing/conversions" element={<Protected perm="digital_printing.material.view"><DpConversions /></Protected>} />
      <Route path="/notifications" element={<Protected><Alerts /></Protected>} />
      <Route path="/reports/:key" element={<Protected><Reports /></Protected>} />
      <Route path="/finance/cash-bank" element={<Protected perm="finance.view"><CashBank /></Protected>} />
      <Route path="/finance/dashboard" element={<Protected perm="finance.view"><FinanceDashboard /></Protected>} />
      <Route path="/finance/reports" element={<Protected perm="finance.view"><FinanceReports /></Protected>} />
      <Route path="/finance/owner-summary" element={<Protected perm="dashboard.owner"><OwnerFinanceSummary /></Protected>} />
      <Route path="/finance/assets" element={<Protected perm="finance.view"><AssetRegister /></Protected>} />
      <Route path="/finance/budget" element={<Protected perm="budget.view"><Budget /></Protected>} />
      <Route path="/finance/bank-reconciliation" element={<Protected perm="finance.view"><BankReconciliation /></Protected>} />
      <Route path="/finance/control" element={<Protected perm="finance.view"><FinanceControl /></Protected>} />
      <Route path="/hr/employees" element={<Protected perm="hr.view"><HrEmployees /></Protected>} />
      <Route path="/hr/masters" element={<Protected perm="hr.view"><HrMasters /></Protected>} />
      <Route path="/hr/work-hours" element={<Protected perm="hr.view"><HrMasters initialKind="shifts" /></Protected>} />
      <Route path="/hr/attendance" element={<Protected perm="hr.view"><HrAttendance /></Protected>} />
      <Route path="/hr/leave" element={<Protected perm="hr.view"><HrLeave /></Protected>} />
      <Route path="/hr/payroll" element={<Protected perm="hr.payroll.view"><HrPayroll /></Protected>} />
      <Route path="/hr/paid-days-policy" element={<Protected perm="hr.payroll.view"><HrPaidDaysPolicy /></Protected>} />
      <Route path="/oms/dashboard" element={<Protected perm="oms.view"><OmsDashboard /></Protected>} />
      <Route path="/oms/chat" element={<Protected perm="oms.view"><ChatMonitoring /></Protected>} />
      <Route path="/oms/store" element={<Protected perm="oms.view"><MonitoringToko /></Protected>} />
      <Route path="/oms/assessment" element={<Protected perm="oms.view"><DailyAssessment /></Protected>} />
      <Route path="/oms/history" element={<Protected perm="oms.view"><ReportHistory /></Protected>} />
      <Route path="/hr/dashboard" element={<Protected perm="hr.view"><HrDashboard /></Protected>} />
      <Route path="/payables/bills" element={<Protected><SupplierBills /></Protected>} />
      <Route path="/payables/ap" element={<Protected><AccountsPayable /></Protected>} />
      <Route path="/payables/payments" element={<Protected><SupplierPayments /></Protected>} />
      <Route path="/sales/receivables" element={<Protected><SalesReceivables /></Protected>} />
      <Route path="/sales/settlements" element={<Protected><MarketplaceSettlement /></Protected>} />
      <Route path="/costing" element={<Protected><CostingDashboard /></Protected>} />
      <Route path="/costing/calculator" element={<Protected><HppCalculator /></Protected>} />
      <Route path="/costing/fabric-prices" element={<Protected><FabricPrices /></Protected>} />
      <Route path="/costing/yields" element={<Protected><FabricYields /></Protected>} />
      <Route path="/costing/sewing" element={<Protected><SewingCost /></Protected>} />
      <Route path="/costing/components" element={<Protected><OptionalCosts /></Protected>} />
      <Route path="/costing/hpp" element={<Protected><HppVersions /></Protected>} />
      <Route path="/costing/waiting" element={<Protected><WaitingHpp /></Protected>} />
      <Route path="/costing/actual" element={<Protected><ActualCosting /></Protected>} />
      <Route path="/accounting/coa" element={<Protected><ChartOfAccounts /></Protected>} />
      <Route path="/accounting/journals" element={<Protected><Journals /></Protected>} />
      <Route path="/accounting/general-ledger" element={<Protected><GeneralLedger /></Protected>} />
      <Route path="/accounting/trial-balance" element={<Protected><TrialBalance /></Protected>} />
      <Route path="/accounting/periods" element={<Protected><AccountingPeriods /></Protected>} />
      <Route path="/users" element={<Protected><Users /></Protected>} />
      <Route path="/roles" element={<Protected><Roles /></Protected>} />
      <Route path="/audit" element={<Protected><AuditLog /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}
